import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { KuniriIcons, kuniriIconWeight } from '@/lib/icons'

interface DatePickerButtonProps {
  label: string
  value: string
  onChange: (value: string) => void
  min?: string
  max?: string
  disabled?: boolean
}

/**
 * Calendar trigger for the locale-formatted date inputs (D-050).
 *
 * The visible field stays a locale-ordered text input. A real native date
 * input sits behind the calendar button so the platform picker opens on every
 * browser; its value is canonical `YYYY-MM-DD` and never shown directly.
 */
export function DatePickerButton({
  label,
  value,
  onChange,
  min,
  max,
  disabled = false,
}: DatePickerButtonProps) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  function openPicker() {
    const input = inputRef.current
    if (!input) return
    if (typeof input.showPicker === 'function') {
      try {
        input.showPicker()
        return
      } catch {
        // Some browsers refuse showPicker on an invisible control; fall back
        // to the native click path below.
      }
    }
    input.focus({ preventScroll: true })
    input.click()
  }

  return (
    <span className="group absolute inset-y-0 right-0 w-12">
      <input
        ref={inputRef}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        value={value}
        min={min}
        max={max}
        disabled={disabled}
        onChange={(event) => {
          onChange(event.target.value)
          buttonRef.current?.focus({ preventScroll: true })
        }}
        className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
      />
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        aria-label={t('common.openCalendarFor', { field: label })}
        onClick={openPicker}
        className="absolute inset-0 flex items-center justify-center text-muted group-hover:text-teal disabled:cursor-not-allowed disabled:opacity-50"
      >
        <KuniriIcons.calendarBlank
          size={20}
          weight={kuniriIconWeight}
          aria-hidden="true"
        />
      </button>
    </span>
  )
}
